import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
	BuildingStorefrontIcon,
	MapPinIcon,
	EnvelopeIcon,
	PhoneIcon,
	ExclamationTriangleIcon,
	XMarkIcon,
	EyeIcon,
	EyeSlashIcon,
	InformationCircleIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Spinner } from '../../components/ui';
import { staffNetworkApi } from '../../api/staffNetwork';
import useNotificationsStore from '../../stores/notificationsStore';
import type {
	CityState,
	NetworkAreas,
	RotationListing,
	StaffEngagement,
	StaffInvite,
} from '../../types/staffNetwork';

const TRAVEL_MINUTES = 30;

const US_STATES: Record<string, string> = {
	AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
	CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
	HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
	LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
	MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
	NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
	OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
	SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
	WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
};

const fmtDate = (iso: string | null): string => {
	if (!iso) return '';
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const fmtDateTime = (iso: string): string =>
	new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const initialsOf = (name: string) =>
	name
		.split(' ')
		.filter(Boolean)
		.map(w => w[0])
		.join('')
		.slice(0, 2)
		.toUpperCase();

const areaKey = (a: CityState) => `${a.city.trim().toLowerCase()}|${a.state.toUpperCase()}`;

const listAreas = (areas: CityState[]) => {
	const names = areas.map(a => `${a.city}, ${a.state}`);
	if (names.length <= 2) return names.join(' or ');
	return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

// ─── Rotation Search Listing ────────────────────────────────────

interface ListingCardProps {
	listing: RotationListing;
	areas: NetworkAreas | null;
	onChange: (next: RotationListing) => void;
}

const ListingCard = ({ listing, areas, onChange }: ListingCardProps) => {
	const [state, setState] = useState(listing.areas[0]?.state ?? 'NY');
	const [customCity, setCustomCity] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	const stateOptions = useMemo(() => {
		const codes = new Set<string>(Object.keys(US_STATES));
		areas?.states.forEach(s => codes.add(s.code));
		return Array.from(codes).sort();
	}, [areas]);

	const knownCities = useMemo(
		() => areas?.states.find(s => s.code === state)?.cities ?? [],
		[areas, state]
	);

	const has = (city: string, st: string) =>
		listing.areas.some(a => areaKey(a) === areaKey({ city, state: st }));

	const save = async (next: CityState[] | null, visible?: boolean) => {
		if (next && next.length === 0) {
			toast.error('Keep at least one city so owners can find you');
			return;
		}
		setIsSaving(true);
		try {
			const updated = await staffNetworkApi.updateMyListing({
				...(next ? { areas: next } : {}),
				...(visible !== undefined ? { visible } : {}),
			});
			onChange(updated);
			if (visible !== undefined) {
				toast.success(visible ? 'You’re visible in rotation search' : 'You’re hidden from rotation search');
			} else {
				toast.success(`Listed in ${updated.areas.length} cit${updated.areas.length === 1 ? 'y' : 'ies'}`);
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update your listing');
		} finally {
			setIsSaving(false);
		}
	};

	const toggleCity = (city: string) => {
		if (has(city, state)) {
			save(listing.areas.filter(a => areaKey(a) !== areaKey({ city, state })));
		} else {
			save([...listing.areas, { city, state }]);
		}
	};

	const addCustomCity = () => {
		const city = customCity.trim();
		if (!city) return;
		if (has(city, state)) {
			toast('Already listed');
			return;
		}
		setCustomCity('');
		save([...listing.areas, { city, state }]);
	};

	const removeArea = (a: CityState) => save(listing.areas.filter(x => areaKey(x) !== areaKey(a)));

	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
			<div className="flex items-start justify-between gap-4 flex-wrap mb-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white">Rotation Search Listing</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						How salon owners find you when they search for rotation staff
					</p>
				</div>
				<button
					type="button"
					disabled={isSaving}
					onClick={() => save(null, !listing.visible)}
					className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border cursor-pointer transition-colors ${
						listing.visible
							? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 text-emerald-800 dark:text-emerald-200'
							: 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
					}`}
				>
					{listing.visible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
					{listing.visible ? 'Visible' : 'Hidden'}
				</button>
			</div>

			{!listing.visible ? (
				<div className="rounded-xl bg-gray-50 dark:bg-gray-700/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 mb-4">
					Your listing is <strong>hidden</strong>. Salon owners can’t find you in rotation search,
					but you keep the salons you already work at.
				</div>
			) : listing.rotationAt.length === 0 ? (
				<div className="rounded-xl bg-gray-50 dark:bg-gray-700/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 mb-4 flex gap-2">
					<InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
					<span>
						You’re not on <strong>rotation</strong> (on call) at any salon yet, so owners can’t
						find you. Ask your salon to set you as On Call, or accept a rotation invite, to
						appear in search.
					</span>
				</div>
			) : (
				<div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 mb-4 flex gap-2">
					<CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
					<span>
						Owners searching <strong>{listAreas(listing.areas)}</strong> can find you, and so
						can anyone who searches your name, email or phone.
					</span>
				</div>
			)}

			<p className="provider-form__label mb-2">
				Cities you work in{' '}
				<span className="text-gray-400 font-normal">({listing.areas.length} selected)</span>
			</p>
			<div className="flex flex-wrap gap-2 mb-4">
				{listing.areas.map(a => (
					<span
						key={areaKey(a)}
						className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-sm bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
					>
						<MapPinIcon className="w-3.5 h-3.5" /> {a.city}, {a.state}
						<button
							type="button"
							className="p-0.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/60 cursor-pointer disabled:opacity-40"
							disabled={listing.areas.length === 1 || isSaving}
							title={listing.areas.length === 1 ? 'Keep at least one city' : 'Remove'}
							onClick={() => removeArea(a)}
						>
							<XMarkIcon className="w-3.5 h-3.5" />
						</button>
					</span>
				))}
			</div>

			<div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
				<div className="provider-form__group mb-0">
					<label className="provider-form__label">Add cities from</label>
					<select className="provider-form__input" value={state} onChange={e => setState(e.target.value)}>
						{stateOptions.map(code => (
							<option key={code} value={code}>
								{US_STATES[code] ?? code} ({code})
							</option>
						))}
					</select>
				</div>
				<div className="provider-form__group mb-0">
					<label className="provider-form__label">Pick one or more cities</label>
					{knownCities.length > 0 ? (
						<div className="flex flex-wrap gap-2 mb-2">
							{knownCities.map(c => {
								const on = has(c, state);
								return (
									<button
										key={c}
										type="button"
										disabled={isSaving}
										onClick={() => toggleCity(c)}
										className={`px-3 py-1.5 rounded-full text-sm border cursor-pointer transition-colors ${
											on
												? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
												: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
										}`}
									>
										{c}
									</button>
								);
							})}
						</div>
					) : (
						<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
							No salons in {US_STATES[state] ?? state} yet — add a city by name.
						</p>
					)}
					<div className="flex gap-2">
						<input
							type="text"
							className="provider-form__input"
							placeholder="Another city…"
							value={customCity}
							onChange={e => setCustomCity(e.target.value)}
							onKeyDown={e => {
								if (e.key === 'Enter') addCustomCity();
							}}
						/>
						<Button variant="secondary" size="sm" onClick={addCustomCity} disabled={isSaving || !customCity.trim()}>
							Add
						</Button>
					</div>
				</div>
			</div>

			{/* Preview */}
			<p className="provider-form__label mt-5 mb-2">What salon owners see</p>
			<div
				className={`flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${
					listing.listed ? '' : 'opacity-50'
				}`}
			>
				<div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center font-bold flex-shrink-0">
					{initialsOf(`${listing.firstName} ${listing.lastName}`)}
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="font-bold text-gray-900 dark:text-gray-100">
							{listing.firstName} {listing.lastName}
						</span>
						<Badge variant="warning">Rotation</Badge>
					</div>
					<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mt-1">
						{listing.email && (
							<span className="inline-flex items-center gap-1">
								<EnvelopeIcon className="w-3.5 h-3.5" /> {listing.email}
							</span>
						)}
						{listing.phone && (
							<span className="inline-flex items-center gap-1">
								<PhoneIcon className="w-3.5 h-3.5" /> {listing.phone}
							</span>
						)}
						<span>
							<strong>{listing.jobsDone}</strong> jobs done
						</span>
					</div>
					<div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
						On rotation at{' '}
						{listing.rotationAt.length === 0 ? (
							<span className="text-gray-400">no salons yet</span>
						) : (
							listing.rotationAt.map(r => (
								<span
									key={r.providerId}
									className="inline-block ml-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
								>
									{r.businessName}
									{r.city ? ` • ${r.city}, ${r.state}` : ''}
								</span>
							))
						)}
					</div>
					<div className="flex flex-wrap gap-1.5 mt-2">
						{listing.areas.map(a => (
							<span
								key={areaKey(a)}
								className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
							>
								<MapPinIcon className="w-3 h-3" /> {a.city}, {a.state}
							</span>
						))}
					</div>
				</div>
			</div>
			<p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
				Owners search by state and city, or by your name, email or phone. You only show up while
				you’re on <strong>rotation</strong> at a salon and this listing is visible. Change your
				email or phone in My Profile.
			</p>
		</div>
	);
};

// ─── Page ───────────────────────────────────────────────────────

const Salons = () => {
	const [listing, setListing] = useState<RotationListing | null>(null);
	const [areas, setAreas] = useState<NetworkAreas | null>(null);
	const [invites, setInvites] = useState<StaffInvite[]>([]);
	const [engagements, setEngagements] = useState<StaffEngagement[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [busyInviteId, setBusyInviteId] = useState<number | null>(null);
	const setPendingInvites = useNotificationsStore(state => state.setPendingInvites);

	const load = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const [l, inv, eng, a] = await Promise.all([
				staffNetworkApi.myListing(),
				staffNetworkApi.myInvites(false),
				staffNetworkApi.myEngagements(),
				staffNetworkApi.areas().catch(() => ({ states: [] }) as NetworkAreas),
			]);
			setListing(l);
			setInvites(inv);
			setEngagements(eng);
			setAreas(a);
			setPendingInvites(inv.length, inv.length === 1 ? inv[0].businessName : null);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load your salons');
		} finally {
			setIsLoading(false);
		}
	}, [setPendingInvites]);

	useEffect(() => {
		load();
	}, [load]);

	const syncInvites = (next: StaffInvite[]) => {
		setInvites(next);
		setPendingInvites(next.length, next.length === 1 ? next[0].businessName : null);
	};

	const accept = async (invite: StaffInvite) => {
		setBusyInviteId(invite.id);
		try {
			const engagement = await staffNetworkApi.acceptInvite(invite.id);
			syncInvites(invites.filter(i => i.id !== invite.id));
			setEngagements(prev => [...prev, engagement]);
			toast.success(`Joined ${invite.businessName} at ${invite.commissionRate}%`);
			// Rotation status and the preview depend on the new engagement.
			staffNetworkApi.myListing().then(setListing).catch(() => undefined);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to accept the invite');
			load();
		} finally {
			setBusyInviteId(null);
		}
	};

	const decline = async (invite: StaffInvite) => {
		setBusyInviteId(invite.id);
		try {
			await staffNetworkApi.declineInvite(invite.id);
			syncInvites(invites.filter(i => i.id !== invite.id));
			toast.success(`Declined the invite from ${invite.businessName}`);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to decline the invite');
			load();
		} finally {
			setBusyInviteId(null);
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex items-center gap-4 mb-6">
				<div
					className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
					style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
				>
					<BuildingStorefrontIcon className="w-6 h-6 text-white" />
				</div>
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Salons</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
						Where you work, the salons inviting you, and how owners find you
					</p>
				</div>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			) : error ? (
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<Button variant="primary" size="sm" onClick={load}>
						Retry
					</Button>
				</div>
			) : (
				<>
					{/* Invitations */}
					<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
						<div className="flex items-center gap-2 mb-1">
							<h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invitations</h2>
							{invites.length > 0 && <Badge variant="danger">{invites.length}</Badge>}
						</div>
						<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
							Salons that want you on their team — you’re not signed up until you accept
						</p>
						{invites.length === 0 ? (
							<p className="text-sm text-gray-400 text-center py-6">No pending invitations.</p>
						) : (
							<div className="space-y-4">
								{invites.map(inv => {
									const already = inv.currentSalonCount ?? engagements.length;
									const busy = busyInviteId === inv.id;
									return (
										<div
											key={inv.id}
											className="rounded-xl border border-gray-200 dark:border-gray-700 p-4"
										>
											<div className="flex items-center gap-3 mb-3">
												<div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
													{initialsOf(inv.businessName)}
												</div>
												<div className="min-w-0">
													<div className="font-semibold text-gray-900 dark:text-gray-100">
														{inv.businessName}
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
														{inv.locations
															.map(l =>
																[l.locationName, [l.city, l.state].filter(Boolean).join(', ')]
																	.filter(Boolean)
																	.join(' · ')
															)
															.join(' • ')}
														{inv.sentAt ? ` • invited ${fmtDateTime(inv.sentAt)}` : ''}
													</div>
												</div>
											</div>
											<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
												<div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-center">
													<div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
														{inv.commissionRate}%
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">Your commission</div>
												</div>
												<div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-center">
													<div className="text-lg font-bold text-gray-800 dark:text-gray-100">
														{Math.round((100 - inv.commissionRate) * 100) / 100}%
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">Salon keeps</div>
												</div>
												<div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-center">
													<div className="text-lg font-bold text-gray-800 dark:text-gray-100">
														{inv.staffType === 'ON_CALL' ? 'Rotation' : 'Permanent'}
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">Employment</div>
												</div>
												<div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-center">
													<div className="text-sm font-bold text-gray-800 dark:text-gray-100 leading-7">
														{inv.startDate ? fmtDate(inv.startDate) : 'To be agreed'}
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">Start date</div>
												</div>
											</div>
											{inv.message && (
												<p className="text-sm italic text-gray-600 dark:text-gray-300 mb-3">
													“{inv.message}”
												</p>
											)}
											{already > 0 && (
												<div className="flex gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 mb-3">
													<ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
													<span>
														You already work at <strong>{already} salon{already === 1 ? '' : 's'}</strong>.
														Bookings here will need <strong>{TRAVEL_MINUTES} minutes</strong> either side
														of any booking at another location, so your day may get tighter.
													</span>
												</div>
											)}
											<div className="flex gap-2 flex-wrap">
												<Button variant="primary" size="sm" isLoading={busy} onClick={() => accept(inv)}>
													Accept — {inv.commissionRate}%
												</Button>
												<Button variant="danger" size="sm" disabled={busy} onClick={() => decline(inv)}>
													Decline
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{/* Where I work */}
					<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Where I Work</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
							Your commission rate is set per salon, not across all of them
						</p>
						<div className="space-y-2">
							{engagements.map(e => (
								<div
									key={e.staffId}
									className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3"
								>
									<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
										{initialsOf(e.businessName)}
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="font-semibold text-gray-900 dark:text-gray-100">{e.businessName}</span>
											{e.staffTypeName && (
												<Badge variant={e.staffType === 'ON_CALL' ? 'warning' : 'info'}>
													{e.staffType === 'ON_CALL' ? 'Rotation' : e.staffTypeName}
												</Badge>
											)}
											{e.home && <Badge variant="primary">Home salon</Badge>}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
											{e.locations
												.map(l =>
													[l.locationName, [l.city, l.state].filter(Boolean).join(', ')]
														.filter(Boolean)
														.join(' · ')
												)
												.join(' • ') || 'No locations'}
											{e.taskNames && e.taskNames.length > 0 ? ` • ${e.taskNames.join(', ')}` : ' • no tasks assigned yet'}
											{e.hireDate ? ` • since ${fmtDate(e.hireDate)}` : ''}
										</div>
									</div>
									<div className="text-sm font-bold text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
										{e.commissionRate != null ? `${e.commissionRate}%` : '—'}
									</div>
								</div>
							))}
						</div>
					</div>

					{listing && <ListingCard listing={listing} areas={areas} onChange={setListing} />}
				</>
			)}
		</AppLayout>
	);
};

export default Salons;
